// MainPage — 메인 대시보드(날짜줄·지표 4칸·오늘 일정·내 업무 보드·최근 회의 + 위젯·최근 드라이브 파일·사용량 요약).
import { useState, useEffect, useCallback, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import Markdown from '../components/Markdown';
import { canSeeMenu } from '../utils/menuAccess';
import { meetingStatusChip } from '../utils/meetingUtil';
import { dashboardAPI, taskPmAPI, todoAPI } from '../api/client';
import { getUnread, subscribe as subscribeUnread } from '../collabUnreadStore';
import { IconLock, IconRobot } from '@tabler/icons-react';
import { COLORS, TONES, btn } from '../utils/uiStyles';
import { BOARD_PILL, BOARD_TONES } from './taskpm/boardStyle';

const PRIO = { urgent: { l: '긴급', c: COLORS.danger }, high: { l: '높음', c: '#d28b50' }, normal: { l: '보통', c: '#64635f' }, low: { l: '낮음', c: '#92918d' } };
const card = { background: 'var(--gw-surface, #fff)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '8px', padding: '16px 18px' };
const MAIN_WIDGETS_STORAGE_KEY = 'mainWidgets';
const DEFAULT_WIDGET_ORDER = ['usage', 'todos', 'messenger', 'tickets', 'calendar', 'drive', 'mail', 'unread'];

const normalizeWidgetPrefs = (prefs = {}) => ({
  order: Array.isArray(prefs.order) ? prefs.order.filter((key) => typeof key === 'string') : [...DEFAULT_WIDGET_ORDER],
  hidden: Array.isArray(prefs.hidden) ? prefs.hidden.filter((key) => typeof key === 'string') : [],
});

const loadWidgetPrefs = () => {
  try {
    return normalizeWidgetPrefs(JSON.parse(localStorage.getItem(MAIN_WIDGETS_STORAGE_KEY) || '{}'));
  } catch (e) {
    return normalizeWidgetPrefs();
  }
};

// 사용량 그래프 — 기능(source)별 한글 라벨. 호버 시 그날 어떤 기능에 토큰을 얼마 썼는지 분해 표시.
const SRC_LABEL = { chat: 'AI 채팅', messenger: '메신저 @ouri', meeting_summary: 'AI 노트 요약', ticket: 'CS 티켓 AI', ai_draft: 'DB도구 초안', doc_classify: '문서 분류·OCR', doc_collect: '문서 수집', unread_summary: '메신저 요약', mail_summary: '메일 요약', external: '외부 연동' };
const srcLabel = (s) => SRC_LABEL[s] || s || '기타';
function UsageTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;   // {date, tokens, input, output, web, sources}
  const entries = Object.entries(d.sources || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div style={{ background: 'var(--gw-surface, #fff)', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: 8, padding: '8px 10px', fontSize: 11, boxShadow: 'var(--gw-float-shadow, 0 4px 24px #1c191711, 0 1px 3px #1c19170b)', lineHeight: 1.6, minWidth: 140 }}>
      <div style={{ fontWeight: 700, marginBottom: 3 }}>{d.date} · 합계 {(d.tokens || 0).toLocaleString()} 토큰</div>
      {entries.length === 0 && <div style={{ color: 'var(--gw-muted, #92918d)' }}>사용 없음</div>}
      {entries.map(([s, t]) => (
        <div key={s} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--gw-text, #32312e)' }}>
          <span>{srcLabel(s)}</span><b>{Number(t).toLocaleString()}</b>
        </div>
      ))}
      <div style={{ borderTop: '1px solid var(--gw-line, #eeeeec)', marginTop: 4, paddingTop: 4, color: 'var(--gw-muted, #92918d)' }}>입력 {(d.input || 0).toLocaleString()} · 출력 {(d.output || 0).toLocaleString()}{d.web > 0 ? ` · 웹검색 ${d.web}회` : ''}</div>
    </div>
  );
}

// ── 홈(시안 F) — 날짜줄 · 지표 4칸 · 오늘 일정 · 내 업무 보드 · 최근 회의 ──
// 🔴 반응형은 인라인으로 못 한다 — 아래 <style> 한 덩어리가 클래스에 건다.
// 🔴 새 섹션 인라인 style 에 그리드 자동 채움(auto-fit)을 쓰지 않는다 — e2e grid_titles() 가 문서의 첫
//    [style*="auto-fit"] 를 위젯 격자로 집는다. 새 섹션에 <aside> 도 쓰지 않는다(e2e locator("aside")).
// 🔴 ≤1200·≤900 열 규칙은 일정·보드가 둘 다 있을 때(gw-home-work--both)만 건다 — 한 칸뿐이면 전폭이다.
// 🔴 지표 2×2 는 뷰포트(@media)만으로 모자란다 — 601~768 은 모바일 셸(<html> zoom 1.5)이라 레이아웃 폭이 ~400 인데 4칸이었다(#3220).
//    지표 섹션 감싸개(컨테이너 이름 gw-home-metrics) 폭 ≤600 으로도 2×2 를 건다. 이름을 꼭 붙인다 — 칸 article 도 크기
//    컨테이너라 이름 없는 @container 는 값 글자가 칸을 봐 모든 폭에서 25px 로 굳는다. 블록은 @media 600 이 닫힌 뒤 최상위다.
// 🔴 일정+보드 세로 쌓기·보드 2열도 같은 이유로 감싸개(컨테이너 이름 gw-home-work) 폭 ≤600 으로 한 번 더 건다(#3223).
//    선택자 앞에 .gw-home-work-wrap 을 붙인다 — 감싸개 ≤600 인 데스크톱(스크롤바·웹 배율)에서 ≤1200 의 0,2,0 규칙을 이기게.
const LINE = 'var(--gw-line, #eeeeec)';
const MUTED = 'var(--gw-muted, #92918d)';
const TEXT = 'var(--gw-text, #32312e)';
const HOME_CSS = '.gw-home-link{background:none;border:none;padding:0;cursor:pointer;font:inherit;font-size:11px;color:var(--gw-muted,#92918d)}'
  + '.gw-home-link:hover{color:var(--gw-text,#32312e);text-decoration:underline}'
  + '@media (max-width:1200px) and (min-width:901px){.gw-home-work.gw-home-work--both{grid-template-columns:170px minmax(0,1fr)!important;gap:18px!important}.gw-home-work--both .gw-home-schedule{padding-right:15px!important}}'
  + '@media (max-width:900px){.gw-home-metrics article{padding:14px!important}.gw-home-work.gw-home-work--both{grid-template-columns:185px minmax(0,1fr)!important;gap:22px!important}}'
  + '@media (max-width:1200px){.gw-home-work--both .gw-home-board{grid-template-columns:repeat(2,minmax(0,1fr))!important}}'
  + '@media (max-width:600px){'
  + '.gw-home-date{font-size:10px!important}'
  + '.gw-home-metrics{grid-template-columns:repeat(2,minmax(0,1fr))!important}'
  + '.gw-home-metrics article:nth-child(-n+2){border-bottom:1px solid var(--gw-line,#eeeeec)}'
  + '.gw-home-metrics article:nth-child(2){border-right:0!important}'
  + '.gw-home-metrics .gw-metric-value{font-size:25px!important}'
  + '.gw-home-metrics .gw-metric-value.gw-metric-value--small{font-size:15px!important}'
  + '.gw-home-work{display:flex!important;flex-direction:column;gap:22px!important}'
  + '.gw-home-schedule{padding:0 0 14px!important;border-right:0!important;border-bottom:1px solid var(--gw-line,#eeeeec)}'
  + '.gw-home-work:not(.gw-home-work--both) .gw-home-schedule{border-bottom:0!important;padding-bottom:0!important}'
  + '.gw-home-events{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}'
  + '.gw-home-events .gw-home-event{margin:0!important}'
  + '.gw-home-events strong{font-size:11px!important}'
  + '.gw-home-events small{font-size:9px!important}'
  + '.gw-home-schedule-foot{display:none!important}'
  + '.gw-home-board{grid-template-columns:repeat(2,minmax(0,1fr))!important}'
  + '.gw-home-meetings [data-col="rec"]{display:none!important}'
  + '.gw-home-meetings .gw-home-mrow{grid-template-columns:minmax(0,1fr) 24% 100px!important;column-gap:0!important}'
  + '.gw-home-title{font-size:25px!important}'
  + '.gw-home-sub{font-size:12px!important}'
  + '}'
  + '@container gw-home-metrics (max-width:600px){'
  + '.gw-home-metrics{grid-template-columns:repeat(2,minmax(0,1fr))!important}'
  + '.gw-home-metrics article:nth-child(-n+2){border-bottom:1px solid var(--gw-line,#eeeeec)}'
  + '.gw-home-metrics article:nth-child(2){border-right:0!important}'
  + '.gw-home-metrics .gw-metric-value{font-size:25px!important}'
  + '.gw-home-metrics .gw-metric-value.gw-metric-value--small{font-size:15px!important}'
  + '}'
  + '@container gw-home-work (max-width:600px){'
  + '.gw-home-work-wrap .gw-home-work{display:flex!important;flex-direction:column;gap:22px!important}'
  + '.gw-home-work-wrap .gw-home-schedule{padding:0 0 14px!important;border-right:0!important;border-bottom:1px solid var(--gw-line,#eeeeec)}'
  + '.gw-home-work-wrap .gw-home-work:not(.gw-home-work--both) .gw-home-schedule{border-bottom:0!important;padding-bottom:0!important}'
  + '.gw-home-work-wrap .gw-home-events{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}'
  + '.gw-home-work-wrap .gw-home-events .gw-home-event{margin:0!important}'
  + '.gw-home-work-wrap .gw-home-events strong{font-size:11px!important}'
  + '.gw-home-work-wrap .gw-home-events small{font-size:9px!important}'
  + '.gw-home-work-wrap .gw-home-schedule-foot{display:none!important}'
  + '.gw-home-work-wrap .gw-home-board{grid-template-columns:repeat(2,minmax(0,1fr))!important}'
  + '}';

// 날짜줄 — 고정 글자가 아니라 오늘(KST)을 그때그때 만든다(«2026년 10월 25일 일요일 · 아워박스 워크스페이스»).
const homeDateText = () => {
  try {
    const day = new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }).format(new Date());
    return `${day} · 아워박스 워크스페이스`;
  } catch (e) {
    return '아워박스 워크스페이스';
  }
};
// KST 날짜 'YYYY-MM-DD'(offsetDays 일 뒤) — 보드 카드 기한을 «오늘/내일»로 읽는 기준.
const kstDay = (offsetDays = 0) => new Date(Date.now() + (9 * 3600 + offsetDays * 86400) * 1000).toISOString().slice(0, 10);
const dueLabel = (iso) => {
  if (!iso) return '기한 없음';
  if (iso === kstDay(0)) return '오늘';
  if (iso === kstDay(1)) return '내일';
  const [, m, d] = String(iso).split('-');
  return `${Number(m)}월 ${Number(d)}일`;
};
// 일정 when = «MM-DD HH:MM» 또는 «MM-DD»(종일) — 서버가 KST 로 준다.
const eventTime = (when) => {
  const s = String(when || '');
  const i = s.indexOf(' ');
  return i > 0 ? s.slice(i + 1) : '종일';
};
const mmss = (sec) => {
  const n = Number(sec);
  if (!n || n < 0) return '—';
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(Math.floor(n % 60)).padStart(2, '0')}`;
};
// 출고 as_of = 미러의 그날 마지막 등록 시각(naive KST ISO) — 시각 글자만 잘라 쓴다(시간대 변환 없음).
const asOfTime = (iso) => {
  const s = String(iso || '');
  return s.length >= 16 ? s.slice(11, 16) : '';
};
// 토큰 합 — 1만 미만은 그대로(9,999) · 이상은 만·억(30.9만 · 123만 · 1.2억). 1만 미만을 compact 에 넘기면 «1.5천» 이라 가른다.
const fmtTokens = (n) => (n < 1e4 ? n.toLocaleString()
  : new Intl.NumberFormat('ko-KR', { notation: 'compact',
    maximumFractionDigits: (n >= 1e8 || n < 1e6) ? 1 : 0 }).format(n));
// 보드 열마다 카드 수 — ≤600 은 2장(390 보드 2열×3장이 길다). 첫 렌더에 한 번만 판정한다.
const boardPerCol = () => {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width:600px)').matches ? 2 : 3;
  } catch (e) {
    return 3;
  }
};
// 🔴 호출은 then 안에서 한다 — 대역(시험)에 없는 함수가 동기 예외로 렌더를 죽이지 않게.
const safeCall = (fn) => Promise.resolve().then(fn);
const settledData = (r) => (r && r.status === 'fulfilled' && r.value && r.value.data) || null;

const metricLabelStyle = { display: 'block', fontSize: '12px', color: 'var(--gw-secondary, #64635f)', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
// 값 글자 — 4칸이면 칸(크기 컨테이너) 폭을 따라 12~28px · 2×2 는 HOME_CSS 의 25px !important 가 이긴다 · 넘치면 말줄임(#3220).
const metricValueStyle = { display: 'block', fontSize: 'clamp(12px, 17cqi, 28px)', fontWeight: 650, letterSpacing: '-1px', lineHeight: '34px', color: TEXT, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const metricUnitStyle = { fontSize: '12px', letterSpacing: 0, fontWeight: 400, marginLeft: '5px', color: MUTED };
const metricFootStyle = { display: 'block', marginTop: '6px', fontSize: '10px', color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const metricFootStrong = { fontWeight: 500, color: 'var(--gw-green, #539b77)' };
const plainButton = { display: 'block', width: '100%', padding: 0, margin: 0, border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' };
const sectionTitleStyle = { fontSize: '13px', fontWeight: 600, color: TEXT };
const emptyTextStyle = { fontSize: '12px', color: MUTED, padding: '6px 0' };
const boardCardStyle = {
  ...plainButton, boxSizing: 'border-box', padding: '11px 10px', borderRadius: '7px',
  border: `1px solid ${LINE}`, boxShadow: `0 1px 1px ${LINE}`, background: 'var(--gw-surface, #fff)', color: TEXT,
};
// 카드 제목 — 2줄 말줄임 · 공백 없는 긴 토큰도 칸 폭에서 끊어야 말줄임이 걸린다(overflow-wrap:anywhere · #3223).
const boardCardTitleStyle = {
  fontSize: '14px', lineHeight: 1.5, letterSpacing: '-0.3px', marginBottom: '6px',
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere',
};
const avatarStyle = {
  width: '19px', height: '19px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, fontSize: '9px', fontWeight: 600, background: TONES.gray.soft, color: COLORS.textSub,
};
const meetingRowStyle = {
  ...plainButton, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 20% 22% minmax(100px,14%)', alignItems: 'center',
  boxSizing: 'border-box', padding: '10px 0', borderTop: `1px solid ${LINE}`, fontSize: '12px',
};
const meetingCellStyle = { minWidth: 0, paddingRight: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// 지표 한 칸(시안 .metric) — onClick 이 있으면 칸 전체가 단추다.
// 칸 자신이 크기 컨테이너(값 글자 cqi 의 기준)다 · 값이 말줄임돼도 정확한 값은 title 로 남긴다(«—»·small 칸 제외).
function MetricCell({ label, value, unit, foot, onClick, last, small }) {
  const title = (value !== '—' && !small) ? `${value}${unit ? ` ${unit}` : ''}` : undefined;
  const inner = (
    <>
      <span style={metricLabelStyle}>{label}</span>
      <span className={small ? 'gw-metric-value gw-metric-value--small' : 'gw-metric-value'}
        style={small ? { ...metricValueStyle, fontSize: '15px', letterSpacing: 0 } : metricValueStyle}>
        {value}{unit ? <span style={metricUnitStyle}>{unit}</span> : null}
      </span>
      <span style={metricFootStyle}>{foot}</span>
    </>
  );
  return (
    <article aria-label={label} title={title} style={{ minWidth: 0, boxSizing: 'border-box', containerType: 'inline-size', padding: '17px 20px', borderRight: last ? 'none' : `1px solid ${LINE}` }}>
      {onClick ? <button type="button" onClick={onClick} style={plainButton}>{inner}</button> : inner}
    </article>
  );
}

export default function MainPage() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const [dash, setDash] = useState(null);
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [cal, setCal] = useState(null);
  const [drive, setDrive] = useState(null);
  const [unread, setUnread] = useState(null);
  const [mail, setMail] = useState(null);
  const [mailCred, setMailCred] = useState(null);
  const [mailPw, setMailPw] = useState('');
  const [mailBusy, setMailBusy] = useState(false);
  const [mailErr, setMailErr] = useState('');
  const [brief, setBrief] = useState(null);     // 모닝 브리핑(③): {enabled, content, date} | null
  const [briefBusy, setBriefBusy] = useState(false);
  const [widgetSettingsOpen, setWidgetSettingsOpen] = useState(false);
  const [widgetPrefs, setWidgetPrefs] = useState(loadWidgetPrefs);
  const hasDrive = user.role === 'admin' || !!user.drive_access;
  const hasMsg = user.role === 'admin' || !!user.messenger_access;
  // 홈 지표·보드·오늘 일정 — undefined = 불러오는 중 · null = 실패(그 칸만 «—»).
  // 🔴 업무 보드는 taskpm 메뉴가 있을 때만 부른다(명시 허용만이라 대부분 403) · 오늘 일정은 드라이브 권한 + 캘린더 메뉴.
  const canTaskpm = canSeeMenu(user, 'taskpm');
  const showSchedule = hasDrive && canSeeMenu(user, 'calendar');
  const [metrics, setMetrics] = useState(undefined);
  const [myTasks, setMyTasks] = useState(undefined);
  const [todayCal, setTodayCal] = useState(undefined);
  const [perCol] = useState(boardPerCol);
  const [unreadNow, setUnreadNow] = useState(getUnread);   // 사이드바가 이미 폴링하는 값(새 요청 없음)
  useEffect(() => subscribeUnread(setUnreadNow), []);
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      safeCall(() => dashboardAPI.homeMetrics()),
      canTaskpm ? safeCall(() => taskPmAPI.myTasks({ per_col: perCol })) : Promise.resolve(null),
      showSchedule ? safeCall(() => dashboardAPI.calendarToday()) : Promise.resolve(null),
    ]).then(([m, t, c]) => {
      if (!alive) return;
      setMetrics(settledData(m));
      if (canTaskpm) setMyTasks(settledData(t));
      if (showSchedule) setTodayCal(settledData(c));
    });
    return () => { alive = false; };
  }, [canTaskpm, showSchedule, perCol]);

  const loadTodos = useCallback(() => { todoAPI.list().then((r) => setTodos(r.data)).catch(() => {}); }, []);
  useEffect(() => { dashboardAPI.get().then((r) => setDash(r.data)).catch(() => {}); loadTodos(); }, [loadTodos]);
  useEffect(() => { dashboardAPI.morningBrief().then((r) => setBrief(r.data)).catch(() => setBrief({ enabled: false, content: '' })); }, []);
  const enableBrief = async () => { setBriefBusy(true); try { await dashboardAPI.setMorningBrief(true); const r = await dashboardAPI.morningBrief(); setBrief(r.data); } catch (e) {} finally { setBriefBusy(false); } };
  const disableBrief = async () => { try { await dashboardAPI.setMorningBrief(false); setBrief({ enabled: false, content: '' }); } catch (e) {} };
  const refreshBrief = async () => { setBriefBusy(true); try { const r = await dashboardAPI.morningBriefRefresh(); setBrief(r.data); } catch (e) {} finally { setBriefBusy(false); } };
  useEffect(() => {
    if (hasDrive) {
      dashboardAPI.calendar().then((r) => setCal(r.data)).catch(() => setCal({ events: [] }));
      dashboardAPI.driveRecent().then((r) => setDrive(r.data)).catch(() => setDrive({ files: [] }));
    }
    if (hasMsg) dashboardAPI.unreadSummary().then((r) => setUnread(r.data)).catch(() => setUnread({ count: 0, summary: '' }));
  }, [hasDrive, hasMsg]);
  useEffect(() => {
    dashboardAPI.mailCredStatus().then((r) => {
      setMailCred(r.data);
      if (r.data.connected) dashboardAPI.mail().then((m) => setMail(m.data)).catch(() => setMail({ count: 0, summary: '' }));
    }).catch(() => setMailCred({ connected: false }));
  }, []);

  const connectMail = async () => {
    const pw = mailPw.trim(); if (!pw) return;
    setMailBusy(true); setMailErr('');
    try {
      const r = await dashboardAPI.mailCredSet(pw);
      if (r.data.connected) {
        setMailCred({ connected: true }); setMailPw(''); setMail(null);
        dashboardAPI.mail().then((m) => setMail(m.data)).catch(() => setMail({ count: 0, summary: '' }));
      } else { setMailErr(r.data.error || '연결에 실패했습니다.'); }
    } catch (e) { setMailErr('연결에 실패했습니다.'); }
    setMailBusy(false);
  };
  const disconnectMail = async () => {
    try { await dashboardAPI.mailCredSet(''); setMailCred({ connected: false }); setMail(null); } catch (e) {}
  };

  const addTodo = async () => { const t = newTodo.trim(); if (!t) return; try { await todoAPI.create(t); setNewTodo(''); loadTodos(); } catch (e) {} };
  const toggleTodo = async (t) => { try { await todoAPI.update(t.id, { done: !t.done }); loadTodos(); } catch (e) {} };
  const delTodo = async (t) => { try { await todoAPI.remove(t.id); loadTodos(); } catch (e) {} };

  const usage = (dash && dash.usage_7d) || [];
  const webTotal = usage.reduce((s, d) => s + (d.web || 0), 0);
  const tickets = dash && dash.tickets;
  const mentions = (dash && dash.mentions) || { count: 0 };
  const showMsg = user.role === 'admin' || !!user.messenger_access;
  const ticketRows = tickets && tickets.is_dx && tickets.changed.length ? tickets.changed : (tickets ? tickets.mine : []);
  const ticketTitle = tickets && tickets.is_dx && tickets.changed.length ? '최근 변경된 개발사 티켓' : '내 진행 중 티켓';

  // ── 홈 지표 4칸 — 칸마다 독립(권한 없는 칸은 서버가 키째 빼고, 여기서 대체 칸을 그린다) ──
  const has = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
  const loadingFoot = metrics === undefined ? '불러오는 중' : '불러오지 못했습니다';
  const ship = metrics && metrics.shipments;
  const cs = metrics && metrics.cs;
  const notes = (metrics && metrics.notes) || null;
  const openTodos = todos.filter((t) => !t.done).length;
  let shipCell;
  if (ship && ship.state === 'ok') {
    const at = asOfTime(ship.as_of);
    shipCell = {
      label: '오늘 출고 건수', value: Number(ship.orders || 0).toLocaleString(), unit: '건',
      // 출고 완료율은 미러가 수정까지 따라올 때만 서버가 준다(out_state 는 나중에 바뀌는 컬럼).
      foot: ship.done_pct != null
        ? <><b style={metricFootStrong}>{ship.done_pct}%</b> 출고 완료 · 2.0 기준</>
        : (at ? `${at} 기준 · 2.0` : '2.0 기준'),
    };
  } else if (ship && ship.state === 'preparing') {
    shipCell = { label: '오늘 출고 건수', value: '준비 중', small: true, foot: '출고 집계 준비 중' };
  } else if ((ship && ship.state === 'hidden') || (!ship && !(user.role === 'admin' || user.db_access))) {
    shipCell = { label: '내 할 일', value: openTodos.toLocaleString(), unit: '건', foot: `완료 ${todos.length - openTodos}건` };
  } else {
    shipCell = { label: '오늘 출고 건수', value: '—', foot: loadingFoot };
  }
  let csCell;
  if (cs && cs.mode === 'cs') {
    csCell = {
      label: '미처리 CS 티켓', value: Number(cs.open || 0).toLocaleString(), unit: '건',
      foot: <>우선 확인 <span style={{ color: 'var(--gw-red, #d66b60)' }}>{cs.urgent || 0}건</span> · 담당 배정 {cs.assigned || 0}건</>,
      onClick: () => navigate('/tickets'),
    };
  } else if (cs && cs.mode === 'mine') {
    // CS 티켓 화면은 CS 권한 전용이라(require_cs) 링크를 두지 않는다.
    csCell = { label: '내 CS 요청', value: Number(cs.mine || 0).toLocaleString(), unit: '건', foot: '처리 중인 내 요청' };
  } else {
    csCell = { label: (user.role === 'admin' || user.cs_access) ? '미처리 CS 티켓' : '내 CS 요청', value: '—', foot: loadingFoot };
  }
  let devCell;
  if (metrics ? has(metrics, 'devreq') : canSeeMenu(user, 'devreq')) {
    const d = metrics && metrics.devreq;
    devCell = d
      ? {
        label: '내 개발요청', value: Number(d.open || 0).toLocaleString(), unit: '건',
        foot: (d.by_status || []).length ? d.by_status.map(([name, n]) => `${name} ${n}`).join(' · ') : '남은 요청 없음',
        onClick: () => navigate('/dev-requests'),
      }
      : { label: '내 개발요청', value: '—', foot: loadingFoot };
  } else {
    // 1만 이상은 만·억으로 줄이고(좁은 칸에서 옆 칸 위로 넘치던 것 · #3220) 정확한 값은 칸 아래 줄에 둔다.
    const tokens = usage.reduce((s, d) => s + (d.tokens || 0), 0);
    devCell = {
      label: '이번 주 AI 사용', value: dash ? fmtTokens(tokens) : '—', unit: dash ? '토큰' : '',
      foot: dash && tokens >= 1e4 ? `최근 7일 · ${tokens.toLocaleString()}` : '최근 7일',
    };
  }
  let noteCell;
  if (metrics ? has(metrics, 'notes') : canSeeMenu(user, 'meetings')) {
    noteCell = notes
      ? { label: '최근 AI 노트', value: Number(notes.today || 0).toLocaleString(), unit: '건', foot: `오늘 기록 · AI 요약 완료 ${notes.today_summarized || 0}건`, onClick: () => navigate('/meetings') }
      : { label: '최근 AI 노트', value: '—', foot: loadingFoot };
  } else {
    const mm = unreadNow.messenger || 0;
    const ml = unreadNow.mail || 0;
    noteCell = { label: '안 읽은 메시지', value: (mm + ml).toLocaleString(), unit: '건', foot: `메신저 ${mm} · 메일 ${ml}` };
  }
  const metricCells = [shipCell, csCell, devCell, noteCell];

  // ── 오늘 일정 · 내 업무 보드 ──
  const todayEvents = (todayCal && Array.isArray(todayCal.events)) ? todayCal.events : [];
  const todayMore = (todayCal && Number(todayCal.more)) || 0;
  const bothWork = showSchedule && canTaskpm;
  const boardCols = (myTasks && Array.isArray(myTasks.columns)) ? myTasks.columns : [];
  const boardRest = boardCols.slice(4).reduce((s, c) => s + (Number(c.count) || 0), 0);
  const recentNotes = (notes && Array.isArray(notes.recent)) ? notes.recent.slice(0, 3) : [];
  const saveWidgetPrefs = (nextPrefs) => {
    const normalized = normalizeWidgetPrefs(nextPrefs);
    setWidgetPrefs(normalized);
    try { localStorage.setItem(MAIN_WIDGETS_STORAGE_KEY, JSON.stringify(normalized)); } catch (e) {}
  };
  const widgetRegistry = [
    {
      key: 'usage',
      title: '이번 주 사용량',
      gate: () => true,
      render: () => (
        <div style={card}>
          <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', marginBottom: '4px' }}>이번 주 사용량</div>
          <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', marginBottom: '8px' }}>최근 7일 토큰 · 웹검색 {webTotal}회 · 점에 마우스를 올리면 기능별 사용량</div>
          <div style={{ height: '110px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={usage} margin={{ top: 6, right: 10, left: 4, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#92918d' }} axisLine={false} tickLine={false} />
                <Tooltip content={<UsageTip />} />
                <Line type="monotone" dataKey="tokens" name="토큰" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 3, fill: COLORS.primary }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ),
    },
    {
      key: 'todos',
      title: '할 일',
      gate: () => true,
      render: () => (
        <div style={card}>
          <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', marginBottom: '8px' }}>할 일</div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
            <input value={newTodo} onChange={(e) => setNewTodo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addTodo(); }} placeholder="할 일 추가"
              style={{ flex: 1, minWidth: 0, padding: '7px 10px', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: '5px', fontSize: '13px' }} />
            <button onClick={addTodo} style={{ padding: '7px 12px', background: COLORS.primary, color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>＋</button>
          </div>
          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {todos.length === 0 && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>할 일이 없습니다.</div>}
            {todos.map((t) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '4px 0' }}>
                <input type="checkbox" checked={t.done} onChange={() => toggleTodo(t)} style={{ cursor: 'pointer' }} />
                <span style={{ flex: 1, fontSize: '13px', color: t.done ? 'var(--gw-muted, #92918d)' : 'var(--gw-text, #32312e)', textDecoration: t.done ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.text}</span>
                <button onClick={() => delTodo(t)} style={{ background: 'none', border: 'none', color: 'var(--gw-border, #e5e5e2)', cursor: 'pointer', fontSize: '14px' }}>×</button>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: 'messenger',
      title: '메신저 알림',
      gate: () => showMsg,
      render: () => (
        <div style={{ ...card, cursor: 'pointer' }} onClick={() => navigate('/messenger')}>
          <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', marginBottom: '8px' }}>메신저 알림</div>
          {mentions.count > 0 ? (
            <div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: COLORS.selectedText }}>{mentions.count}<span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gw-secondary, #64635f)' }}> 건</span></div>
              <div style={{ fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginTop: '2px' }}>나를 호출한 미확인 멘션 · 눌러서 확인 →</div>
            </div>
          ) : (
            <div style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)', padding: '14px 0' }}>새 멘션이 없습니다.</div>
          )}
        </div>
      ),
    },
    {
      key: 'tickets',
      title: 'CS 티켓',
      gate: () => tickets && canSeeMenu(user, 'tickets'),
      render: () => (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>{ticketTitle}</div>
            <button onClick={() => navigate('/tickets')} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm'), marginLeft: 'auto' }}>전체 →</button>
          </div>
          {ticketRows.length === 0 && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '10px 0' }}>해당 티켓이 없습니다.</div>}
          {ticketRows.map((t) => (
            <div key={t.id} onClick={() => navigate('/tickets')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', cursor: 'pointer', borderTop: '1px solid var(--gw-line, #eeeeec)' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: PRIO[t.priority]?.c, width: '28px', flexShrink: 0 }}>{PRIO[t.priority]?.l}</span>
              <span style={{ flex: 1, fontSize: '13px', color: 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
              <span style={{ fontSize: '11px', color: 'var(--gw-muted, #92918d)', flexShrink: 0 }}>{t.shipper || t.center || ''}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'calendar',
      title: '다가오는 일정',
      gate: () => hasDrive,
      render: () => (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>다가오는 일정</div>
            <button onClick={() => navigate('/calendar')} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm'), marginLeft: 'auto' }}>캘린더 →</button>
          </div>
          {cal === null && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>불러오는 중…</div>}
          {cal && cal.events.length === 0 && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>다가오는 일정이 없습니다.</div>}
          {cal && cal.events.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', padding: '4px 0', fontSize: '13px', borderTop: i ? '1px solid var(--gw-line, #eeeeec)' : 'none' }}>
              <span style={{ color: 'var(--gw-purple-ink, #7c5e96)', fontWeight: 600, flexShrink: 0, width: '78px' }}>{e.when}</span>
              <span style={{ color: 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.summary}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'drive',
      title: '드라이브 최근 파일',
      gate: () => hasDrive,
      render: () => (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>드라이브 최근 파일</div>
            <button onClick={() => navigate('/drive-app')} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm'), marginLeft: 'auto' }}>드라이브 →</button>
          </div>
          {drive === null && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>불러오는 중…</div>}
          {drive && drive.files.length === 0 && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>최근 파일이 없습니다.</div>}
          {drive && drive.files.map((f, i) => (
            <div key={i} onClick={() => navigate('/drive-app')} style={{ display: 'flex', padding: '4px 0', fontSize: '13px', cursor: 'pointer', borderTop: i ? '1px solid var(--gw-line, #eeeeec)' : 'none' }}>
              <span style={{ color: 'var(--gw-text, #32312e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'mail',
      title: '새 메일 요약',
      gate: () => true,
      render: () => (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', display: 'flex', alignItems: 'center', gap: '6px' }}><IconRobot size={16} stroke={1.8} aria-hidden="true" />새 메일 요약</div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {mailCred && mailCred.connected && (
                <button onClick={disconnectMail} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm') }}>연결 해제</button>
              )}
              {/* 요약만 보여 주고 정작 메일함으로 갈 데가 없었다(다른 위젯은 전부 '→' 가 있다).
                  메뉴 권한이 없으면 링크도 감춘다 — 눌러도 못 들어가는 링크는 두지 않는다. */}
              {canSeeMenu(user, 'mail2') && (
                <button onClick={() => navigate('/mail2')} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm') }}>메일 →</button>
              )}
            </div>
          </div>
          {mailCred === null && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>불러오는 중…</div>}
          {mailCred && !mailCred.connected && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)', marginBottom: '8px', lineHeight: 1.5 }}>
                다우오피스 메일을 연결하면 읽지 않은 메일을 AI가 요약해 드립니다.
              </div>
              <input type="password" value={mailPw} onChange={(e) => setMailPw(e.target.value)} placeholder="다우오피스 메일 비밀번호"
                onKeyDown={(e) => e.key === 'Enter' && connectMail()}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: '5px', fontSize: '13px' }} />
              {mailErr && <div style={{ fontSize: '11px', color: COLORS.danger, marginTop: '5px' }}>{mailErr}</div>}
              <button onClick={connectMail} disabled={mailBusy} style={{ marginTop: '7px', width: '100%', padding: '7px', background: COLORS.primary, color: 'white', border: 'none', borderRadius: '5px', fontSize: '12px', cursor: mailBusy ? 'default' : 'pointer', opacity: mailBusy ? 0.6 : 1 }}>{mailBusy ? '연결 중…' : '메일 연결'}</button>
              <div style={{ fontSize: '10px', color: 'var(--gw-muted, #92918d)', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}><IconLock size={11} stroke={1.8} aria-hidden="true" />비밀번호는 암호화 저장되며 메일 조회에만 쓰입니다.</div>
            </div>
          )}
          {mailCred && mailCred.connected && mail === null && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>AI가 정리 중…</div>}
          {mailCred && mailCred.connected && mail && mail.error && <div style={{ fontSize: '12px', color: COLORS.danger, padding: '8px 0' }}>{mail.error}</div>}
          {mailCred && mailCred.connected && mail && !mail.error && mail.count === 0 && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>읽지 않은 메일이 없습니다.</div>}
          {mailCred && mailCred.connected && mail && !mail.error && mail.count > 0 && (
            <div>
              <div style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: '140px', overflowY: 'auto' }}><Markdown compact muted>{mail.summary || `${mail.count}건의 새 메일`}</Markdown></div>
              <div style={{ fontSize: '12px', color: COLORS.info, marginTop: '8px' }}>읽지 않은 메일 {mail.count}건</div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'unread',
      title: '안 읽은 메시지 요약',
      gate: () => hasMsg,
      render: () => (
        <div style={card}>
          <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}><IconRobot size={16} stroke={1.8} aria-hidden="true" />안 읽은 메시지 요약</div>
          {unread === null && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>AI가 정리 중…</div>}
          {unread && unread.count === 0 && <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', padding: '8px 0' }}>안 읽은 메시지가 없습니다.</div>}
          {unread && unread.count > 0 && (
            <div>
              <div style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: '140px', overflowY: 'auto' }}><Markdown compact muted>{unread.summary || `${unread.count}건의 새 메시지`}</Markdown></div>
              <button type="button" onClick={() => navigate('/messenger')} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm'), marginTop: '8px' }}>전체 {unread.count}건 · 메신저에서 보기 →</button>
            </div>
          )}
        </div>
      ),
    },
  ];
  const widgetByKey = Object.fromEntries(widgetRegistry.map((widget) => [widget.key, widget]));
  const allWidgetKeys = widgetRegistry.map((widget) => widget.key);
  const savedWidgetOrder = widgetPrefs.order.filter((key, index) => widgetByKey[key] && widgetPrefs.order.indexOf(key) === index);
  const normalizedWidgetOrder = [...savedWidgetOrder, ...allWidgetKeys.filter((key) => !savedWidgetOrder.includes(key))];
  const hiddenWidgets = new Set(widgetPrefs.hidden.filter((key) => widgetByKey[key]));
  const availableWidgets = normalizedWidgetOrder.map((key) => widgetByKey[key]).filter((widget) => widget.gate());
  const availableWidgetKeys = availableWidgets.map((widget) => widget.key);
  const widgetsToRender = availableWidgets.filter((widget) => !hiddenWidgets.has(widget.key));
  const toggleWidgetVisibility = (key, shouldShow) => {
    const nextHidden = new Set(widgetPrefs.hidden.filter((item) => widgetByKey[item]));
    if (shouldShow) nextHidden.delete(key);
    else nextHidden.add(key);
    saveWidgetPrefs({ ...widgetPrefs, order: normalizedWidgetOrder, hidden: Array.from(nextHidden) });
  };
  const moveWidget = (key, direction) => {
    const currentIndex = availableWidgetKeys.indexOf(key);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= availableWidgetKeys.length) return;
    const nextAvailableKeys = [...availableWidgetKeys];
    [nextAvailableKeys[currentIndex], nextAvailableKeys[nextIndex]] = [nextAvailableKeys[nextIndex], nextAvailableKeys[currentIndex]];
    const availableKeySet = new Set(availableWidgetKeys);
    let availableIndex = 0;
    const nextOrder = normalizedWidgetOrder.map((currentKey) => {
      if (!availableKeySet.has(currentKey)) return currentKey;
      const nextKey = nextAvailableKeys[availableIndex];
      availableIndex += 1;
      return nextKey;
    });
    saveWidgetPrefs({ ...widgetPrefs, order: nextOrder, hidden: widgetPrefs.hidden.filter((item) => widgetByKey[item]) });
  };

  return (
    <AppShell app="home">
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: 'var(--gw-bg, #fff)', display: 'flex', flexDirection: 'column' }}>
        <Announcements scope="home" />
        <div style={{ maxWidth: '1080px', width: '100%', margin: '0 auto', padding: 'clamp(20px, 4vw, 42px) clamp(20px, 4vw, 54px) 46px', boxSizing: 'border-box' }}>
          <style>{HOME_CSS}</style>
          {/* 날짜줄 — 머리 위(시안 home-date → pageHead) */}
          <div className="gw-home-date" style={{ fontSize: '11px', color: MUTED, marginBottom: '17px' }}>{homeDateText()}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <h1 className="gw-home-title" style={{ fontSize: 'clamp(26px, 3.2vw, 36px)', fontWeight: 720, letterSpacing: '-1.3px', lineHeight: 1.2, color: 'var(--gw-text, #32312e)', margin: '0 0 8px' }}>안녕하세요{user.name ? `, ${user.name}님` : ''}</h1>
              <p className="gw-home-sub" style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)', lineHeight: 1.5, margin: '0 0 24px' }}>오늘의 현황입니다.</p>
            </div>
            <button type="button" onClick={() => setWidgetSettingsOpen((open) => !open)} aria-expanded={widgetSettingsOpen} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet'), flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>⚙ 위젯 설정</button>
          </div>

          {/* 지표 4칸(시안 .metrics) — 칸 사이 세로선 · 마지막 칸 없음 · 뷰포트 ≤600 또는 감싸개 폭 ≤600 이면 2×2.
              감싸개가 크기 컨테이너(gw-home-metrics)다 — 섹션 자신은 자기에게 질의 못 한다. 컨테이너는 여백이 안 겹쳐
              (레이아웃 격리) 섹션의 아래 여백 28px 을 감싸개로 옮겼다(#3220). */}
          <div className="gw-home-metrics-wrap" style={{ containerType: 'inline-size', containerName: 'gw-home-metrics', marginBottom: '28px' }}>
            <section aria-label="오늘 지표" className="gw-home-metrics"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', border: `1px solid ${LINE}`, borderRadius: '8px', overflow: 'hidden' }}>
              {metricCells.map((cell, i) => <MetricCell key={cell.label} {...cell} last={i === metricCells.length - 1} />)}
            </section>
          </div>

          {/* 오늘 일정 + 내 업무 보드(시안 .home-work) — 한쪽만 있으면 그 칸이 전폭이다.
              감싸개가 크기 컨테이너(gw-home-work)다 — 모바일 셸 601~768(zoom 1.5) 은 뷰포트 @media 600 이 안 걸려서 감싸개 폭으로 묻는다.
              지표와 같이 섹션의 아래 여백을 감싸개로 옮겼다(#3223). */}
          {(showSchedule || canTaskpm) && (
            <div className="gw-home-work-wrap" style={{ containerType: 'inline-size', containerName: 'gw-home-work', marginBottom: notes ? 0 : '28px' }}>
              <section className={bothWork ? 'gw-home-work gw-home-work--both' : 'gw-home-work'}
                style={{ display: 'grid', gridTemplateColumns: bothWork ? '205px minmax(0,1fr)' : 'minmax(0,1fr)', gap: '27px' }}>
                {showSchedule && (
                  <div className="gw-home-schedule" style={{ minWidth: 0, paddingRight: bothWork ? '22px' : 0, borderRight: bothWork ? `1px solid ${LINE}` : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '14px' }}>
                      <span style={sectionTitleStyle}>오늘 일정</span>
                      {todayCal && <span style={{ fontSize: '11px', color: MUTED }}>{todayEvents.length + todayMore}</span>}
                    </div>
                    {todayCal === undefined && <div style={emptyTextStyle}>불러오는 중…</div>}
                    {todayCal === null && <div style={emptyTextStyle}>—</div>}
                    {todayCal && todayEvents.length === 0 && <div style={{ ...emptyTextStyle, marginBottom: '14px' }}>오늘 일정이 없습니다</div>}
                    {todayEvents.length > 0 && (
                      <div className="gw-home-events">
                        {todayEvents.map((ev, i) => (
                          <div key={`${ev.when}-${i}`} className="gw-home-event" style={{ display: 'grid', gridTemplateColumns: '7px minmax(0,1fr)', gap: '8px', margin: '0 0 20px' }}>
                            <span aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', marginTop: '6px', background: TONES[BOARD_PILL[i % 4]].ink }} />
                            <span style={{ minWidth: 0 }}>
                              <strong style={{ display: 'block', fontSize: '12px', fontWeight: 500, lineHeight: 1.5, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.summary}</strong>
                              <small style={{ display: 'block', fontSize: '10px', color: MUTED }}>{eventTime(ev.when)}</small>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {todayMore > 0 && <div style={{ fontSize: '11px', color: MUTED, margin: '0 0 14px' }}>외 {todayMore}건</div>}
                    <div className="gw-home-schedule-foot" style={{ borderTop: `1px solid ${LINE}`, paddingTop: '14px', fontSize: '11px', color: MUTED, lineHeight: 1.7 }}>
                      <button type="button" className="gw-home-link" onClick={() => navigate('/calendar')}>캘린더 보기</button>
                    </div>
                  </div>
                )}
                {canTaskpm && (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <div className="board-tabs" role="tablist" aria-label="업무 보드" style={{ display: 'flex', gap: '14px' }}>
                        <button type="button" role="tab" aria-selected="true"
                          style={{ padding: '0 0 6px', border: 'none', borderBottom: `2px solid ${TEXT}`, background: 'none', font: 'inherit', fontSize: '13px', fontWeight: 600, color: TEXT, cursor: 'default' }}>내 업무</button>
                      </div>
                      <button type="button" className="gw-home-link" style={{ marginLeft: 'auto' }} onClick={() => navigate('/tasks')}>업무 보기</button>
                    </div>
                    {myTasks === undefined && <div style={emptyTextStyle}>불러오는 중…</div>}
                    {myTasks === null && <div style={emptyTextStyle}>—</div>}
                    {myTasks && boardCols.length === 0 && <div style={emptyTextStyle}>담당 업무가 없습니다</div>}
                    {boardCols.length > 0 && (
                      <div className="gw-home-board" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: '9px' }}>
                        {boardCols.slice(0, 4).map((col, i) => {
                          const tone = TONES[BOARD_PILL[i % 4]];
                          const cards = Array.isArray(col.tasks) ? col.tasks : [];
                          const moreCards = (Number(col.count) || 0) - cards.length;
                          // 🔴 jsdom 은 background 의 var() 값을 버린다 → 톤 순서는 data-board-tone 으로도 남긴다(칸반과 같다).
                          return (
                            <section key={col.label} aria-label={`${col.label} 열`} data-board-tone={i % 4}
                              style={{ boxSizing: 'border-box', minWidth: 0, padding: '8px 6px 6px', background: BOARD_TONES[i % 4], borderRadius: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 1px 10px', minHeight: '22px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 5px', borderRadius: '20px', fontSize: '10px', lineHeight: '18px', whiteSpace: 'nowrap', background: tone.soft, color: tone.ink, minWidth: 0 }}>
                                  <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.label}</span>
                                </span>
                                <span style={{ fontSize: '10px', color: MUTED }}>{col.count}</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {cards.map((t) => (
                                  <button key={t.id} type="button" data-testid={`home-task-${t.id}`} onClick={() => navigate(`/tasks/${t.project_id}?task=${t.id}`)} style={boardCardStyle}>
                                    <span style={boardCardTitleStyle}>{t.title}</span>
                                    <span style={{ display: 'block', fontSize: '10px', color: MUTED, marginBottom: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.project_name}</span>
                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', minHeight: '19px' }}>
                                      <span style={{ fontSize: '10px', color: MUTED }}>{dueLabel(t.due_date)}</span>
                                      {t.assignee_initial && <span aria-hidden="true" style={avatarStyle}>{t.assignee_initial}</span>}
                                    </span>
                                  </button>
                                ))}
                              </div>
                              {moreCards > 0 && (
                                <button type="button" className="gw-home-link" style={{ marginTop: '8px', padding: '0 4px', fontSize: '10px' }} onClick={() => navigate('/tasks')}>{moreCards}건 더</button>
                              )}
                            </section>
                          );
                        })}
                      </div>
                    )}
                    {boardRest > 0 && <div style={{ fontSize: '11px', color: MUTED, marginTop: '8px' }}>그 밖 {boardRest}건</div>}
                  </div>
                )}
              </section>
            </div>
          )}

          {/* 최근 회의 3행(시안 .recent-meetings) — 요약 칸은 AI 노트 목록과 같은 분기(상태 알약 · 요약 있음/전) */}
          {notes && (
            <section aria-label="최근 회의" className="gw-home-meetings" style={{ marginTop: '30px', marginBottom: '28px' }}>
              <div style={{ ...sectionTitleStyle, marginBottom: '10px' }}>최근 회의</div>
              {recentNotes.length === 0 && <div style={emptyTextStyle}>최근 회의가 없습니다</div>}
              {recentNotes.map((r) => {
                const chip = (r.no_speech || r.status !== 'done') ? meetingStatusChip(r) : null;
                return (
                  <button key={r.id} type="button" className="gw-home-mrow" onClick={() => navigate(`/meetings?open=${r.id}`)} style={meetingRowStyle}>
                    <span data-col="title" style={{ ...meetingCellStyle, color: TEXT, fontWeight: 500 }}>{r.title || '제목 없음'}</span>
                    <span data-col="folder" style={{ ...meetingCellStyle, color: MUTED }}>{r.folder_name || '미분류'}</span>
                    <span data-col="rec" style={{ ...meetingCellStyle, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{mmss(r.duration_sec)}</span>
                    <span data-col="sum" style={{ ...meetingCellStyle, paddingRight: 0 }}>
                      {chip
                        ? <span style={{ display: 'inline-block', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box', maxWidth: '100%', padding: '2px 7px', borderRadius: '999px', background: chip.bg, color: chip.color, fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>{chip.label}</span>
                        : <span style={{ color: r.has_summary ? TEXT : MUTED }}>{r.has_summary ? '요약 있음' : '요약 전'}</span>}
                    </span>
                  </button>
                );
              })}
            </section>
          )}

          {/* 모닝 브리핑(③) — 옵트인. 일정·안읽음·할일(+담당화주 현황) 한 단락 요약 */}
          <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
              <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>오늘의 모닝 브리핑</div>
              {brief && brief.enabled && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                  <button onClick={refreshBrief} disabled={briefBusy} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm') }}>{briefBusy ? '생성 중…' : '새로고침'}</button>
                  <button onClick={disableBrief} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm') }}>끄기</button>
                </div>
              )}
            </div>
            {brief === null && <div style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)', padding: '6px 0' }}>불러오는 중…</div>}
            {brief && !brief.enabled && (
              <div>
                <div style={{ fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginBottom: '10px', lineHeight: 1.6 }}>매일 아침 일정·안 읽은 메시지·할 일(담당 화주가 지정돼 있으면 현황까지)을 한 단락으로 요약해 드립니다.</div>
                <button onClick={enableBrief} disabled={briefBusy} style={{ padding: '7px 14px', background: COLORS.primary, color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>{briefBusy ? '준비 중…' : '모닝 브리핑 켜기'}</button>
              </div>
            )}
            {brief && brief.enabled && (
              brief.content
                ? <div style={{ fontSize: '14px', color: 'var(--gw-text, #32312e)', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}><Markdown compact muted>{brief.content}</Markdown></div>
                : <div style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)', padding: '6px 0' }}>{briefBusy ? '브리핑 생성 중…' : '오늘 브리핑을 준비 중입니다. 새로고침을 눌러 주세요.'}</div>
            )}
          </div>

          {widgetSettingsOpen && (
            <div style={{ ...card, marginBottom: '14px', padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>위젯 설정</div>
                <button type="button" onClick={() => setWidgetSettingsOpen(false)} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm'), marginLeft: 'auto' }}>닫기</button>
              </div>
              <div style={{ display: 'grid', gap: '6px' }}>
                {availableWidgets.map((widget, index) => (
                  <div key={widget.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderTop: index ? '1px solid var(--gw-line, #eeeeec)' : 'none' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, fontSize: '13px', color: 'var(--gw-text, #32312e)' }}>
                      <input type="checkbox" checked={!hiddenWidgets.has(widget.key)} onChange={(e) => toggleWidgetVisibility(widget.key, e.target.checked)} style={{ cursor: 'pointer' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{widget.title}</span>
                    </label>
                    <button type="button" onClick={() => moveWidget(widget.key, -1)} disabled={index === 0} style={{ width: '28px', height: '26px', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: '5px', background: 'var(--gw-surface, #fff)', color: 'var(--gw-text, #32312e)', cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.45 : 1 }}>↑</button>
                    <button type="button" onClick={() => moveWidget(widget.key, 1)} disabled={index === availableWidgets.length - 1} style={{ width: '28px', height: '26px', border: '1px solid var(--gw-border, #e5e5e2)', borderRadius: '5px', background: 'var(--gw-surface, #fff)', color: 'var(--gw-text, #32312e)', cursor: index === availableWidgets.length - 1 ? 'default' : 'pointer', opacity: index === availableWidgets.length - 1 ? 0.45 : 1 }}>↓</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 위젯 대시보드 — alignItems:start = 각 위젯이 자기 데이터만큼만 높이(동일높이 강제 안 함) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '14px', marginBottom: '26px', alignItems: 'start' }}>
            {widgetsToRender.map((widget) => cloneElement(widget.render(), { key: widget.key }))}
          </div>

          <div style={{ marginTop: '24px', background: COLORS.primarySoft, border: `1px solid ${COLORS.primaryBorder}`, borderRadius: '8px', padding: '14px 18px', fontSize: '13px', color: COLORS.text, lineHeight: 1.7 }}>
            <b>처음이신가요?</b> 상단 <b>사용 안내</b>에서 각 기능 사용법을 확인하세요. 모바일은 <b>‘홈 화면에 추가’</b>로 설치하면 앱처럼 쓰고 알림도 받습니다.
          </div>
        </div>
      </div>
    </AppShell>
  );
}
